import {ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, Input, OnDestroy, Output,} from '@angular/core';
import {DesignApiService, ErrorListPageResponse, Job, JobApiService, JobStatus,} from '@ansys/cloud-angular-client';
import {ActionContributionsService} from '@ansys-internal/awf-angular/contributions';
import {DialogService, NavigateAction, SnackBarService,} from '@ansys-internal/awf-angular/shared';
import {ActiveSectionState} from '@ansys-internal/awf-angular/workspace';
import {ColorBackground, ColorForeground,} from '@ansys-internal/awc-angular/core';
import {IconComponent, Icons, IconSize,} from '@ansys-internal/awc-angular/icons';
import {ProgressBarComponent, ProgressBarItem,} from '@ansys-internal/awc-angular/loaders';
import {JobsService, RequirementsSolved} from 'src/api';
import {firstValueFrom, lastValueFrom, ReplaySubject, startWith, takeUntil,} from 'rxjs';
import {JobCancelProcessing, JobMetaData, JobWithExtra, LoadAllJobs, SetJobs, UpdateJob, UpdateJobProgress, UpdateJobStatus, UpdateJobTimes,} from '../../../actions';
import {RequirementsService} from 'src/app/shared/services/requirements.service';
import {ResultsService} from 'src/app/shared/services/results.services';
import {JobErrorDialogComponent} from '../../dialogs/job-error-dialog/job-error-dialog.component';
import {JobLoadingStatus} from '../../../shared/enums/job-loading-status.enum';
import {ConfirmComponent} from '../../dialogs/confirm/confirm.component';
import {AppThemeState, SelectedAccountState, State,} from '@ansys-internal/awf-angular/store';
import {ActiveConceptState} from '../../../state/lib/active-concept.state';
import {PlotService} from '../../../shared/services/plot.service';
import {CommonModule} from '@angular/common';
import {LoadingComponent} from '../../dialogs/loading/loading.component';
import {UpdateOneInputComponent} from '../../dialogs/update-one-input/update-one-input.component';
import {SharedModule} from '../../../shared/shared.module';
import {CheckboxComponent} from '@ansys-internal/awc-angular/forms';
import {ResultItemComponent} from './result-item/result-item.component';
import {PageState, PageStateExecuteEnum, PageStateModel,} from '../../../state/lib/app-page.state';
import {ConceptSections, ConceptSubSection} from '../../../shared/enums';
import {CevActionsService} from '../../../shared/services/cev-actions.service';
import {CompareJobsState} from '../../../state/lib/compare-jobs.state';
import {DataDisplayService} from '../../../shared/services/data-display.service';
import {ActivatedRoute} from '@angular/router';
import jobStatus = JobStatus.jobStatus;
import {formatJobDuration} from "../../../shared/tools/common.utils";

export enum ResultType {
  STATIC = 'static',
  DYNAMIC = 'dynamic',
  DRIVE_CYCLE = 'drive_cycle',
}

@Component({
    selector: 'results-list',
    templateUrl: './results-list.component.html',
    styleUrls: ['./results-list.component.scss'],
    imports: [
        ProgressBarComponent,
        CommonModule,
        IconComponent,
        SharedModule,
        ResultItemComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResultsListComponent implements OnDestroy {
  @Input() selectedItems: string[] = [];
  @Output() selectedItemsChange: EventEmitter<string[]> = new EventEmitter();
  @Input() compareCheckboxesVisible: boolean = false;
  @Output() selectedJobDuration: EventEmitter<string> = new EventEmitter<string>();
  protected jobsList: JobWithExtra[] = []; // The extra is to add the 'cancelling' property
  protected readonly Icons = Icons;
  protected iconSmall: IconSize = IconSize.SMALL;
  protected iconXSmall: IconSize = IconSize.X_SMALL;
  protected selectedJob: string = '';
  protected selectedResult: string = '';
  protected $resultsLoaded: boolean = false;
  protected ResultType = ResultType;
  protected maxHeight: number = 500;
  protected results: RequirementsSolved[] = [];
  protected cliVersion: string | undefined;
  protected progressBars: { [key: string]: ProgressBarItem[] } = {};
  protected barlabels: { [key: string]: string } = {};
  protected trustedStatus: { [key: string]: string } = {};
  protected loadingBar: { [key: string]: boolean } = {};
  protected hasJobProgressSocket: { [key: string]: boolean } = {};
  protected theme!: string;
  protected readonly Object = Object;
  protected currentDate = new Date();
  protected page!: PageStateModel;
  private designId!: string;
  private toupdate: boolean = false;
  private ngUnsubscribe: ReplaySubject<boolean> = new ReplaySubject(1);
  protected readonly ColorBackground = ColorBackground;
  protected readonly ConceptSections = ConceptSections;
  protected readonly ConceptSubSection = ConceptSubSection;
  protected readonly ColorForeground = ColorForeground;
  protected etaDisplay: { [jobId: string]: string | undefined } = {};
  protected jobDurations: { [jobId: string]: string } = {};

  constructor(
    private requirementService: RequirementsService,
    private jobService: JobsService,
    private jobApiService: JobApiService,
    private _cdr: ChangeDetectorRef,
    private snackbar: SnackBarService,
    private resultsService: ResultsService,
    private actions: ActionContributionsService,
    private dialog: DialogService,
    private elRef: ElementRef,
    private dialogService: DialogService,
    private state: State,
    private actionService: ActionContributionsService,
    private plotService: PlotService,
    private cevActions: CevActionsService,
    private activeRoute: ActivatedRoute,
    private displayService: DataDisplayService,
    private designService: DesignApiService
  ) {
    this.requirementService.selectFromPlot
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((id: string) => {
        if (this.results && id) {
          const result = this.results.find((result) => result.id === id);
          this.selectResult(result);
        }
      });

    this.state
      .get(AppThemeState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((theme: string) => {
        if (theme && this.theme !== theme) {
          const isDarkMode =
            theme === 'dark' ||
            (theme === 'auto' &&
              window.matchMedia &&
              window.matchMedia('(prefers-color-scheme: dark)').matches);

          this.theme = isDarkMode ? 'dark' : ('light' as string);
        }
      });

    effect(async () => {
      const selectedYaxis = this.requirementService.requirementYAxis();
      this.onYAxisChange(selectedYaxis);
    });
  }

  async ngOnInit(): Promise<void> {
    this.state
      .get(PageState)
      .pipe(
        startWith(this.state.value(PageState)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((data: PageStateModel) => {
        this.page = data;
        this.calcMaxHeight();
        this._cdr.detectChanges();
      });

    this.state
      .get(CompareJobsState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.calcMaxHeight();
      });

    this.actions
      .get(UpdateJobProgress)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: [string, number]) => this.updateJobProgress(data));

    this.actions
      .get(UpdateJobTimes)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: [string, number, number]) => {
        const [jobId, minTime, maxTime] = data;

        const minDisplay = formatJobDuration(minTime);
        const maxDisplay = formatJobDuration(maxTime);

        let display: string | undefined;
        if (minDisplay === 'NaN min' || maxDisplay === 'NaN min') {
          display = undefined;
        } else {
          if (maxTime === 0) {
            display = ``;
          } else if (minDisplay === maxDisplay) {
            display = `Estimated: ${minDisplay}`;
          } else {
            display = `Estimated: ${minDisplay} – ${maxDisplay}`;
          }
        }

        this.etaDisplay[jobId] = display;
      });

    this.actions
      .get(UpdateJobStatus)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: [string, string]) => this.updateJobStatus(data));

    this.actions
      .get(UpdateJob)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: Job) => this.updateJobFromStore(data));

    this.actions
      .get(JobMetaData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((jobsMeta: Map<string, string>) => {
        if (!jobsMeta || jobsMeta.size === 0) {
          this.updateJobName(jobsMeta);
        }
      });

    this.actions.execute(SetJobs);
    this.actions
      .get(SetJobs)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((jobs: Job[]) => this.setJobs(jobs));

    const activeSection = this.state.value(ActiveSectionState);
    if (activeSection?.urlParams && activeSection?.urlParams?.length > 1) {
      const concept = this.state.value(ActiveConceptState);
      if (concept) {
        if (this.page.subsection !== ConceptSubSection.COMPARISON_TOOL) {
          const jobId = activeSection?.urlParams[1];
          if (concept.jobs_ids.includes(jobId)) {
            const job = await lastValueFrom(
              this.jobApiService.jobLoadHandler({ jobId })
            );
            this.selectJob(job);
          } else {
            this.snackbar.error('No Job');

            this.deselectJob(true);
          }
        }
      }
    }

    this.resultsService.localDevOutput$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rawResults) => {
        if (rawResults) {
          this.loadLocalDevResults(rawResults);
        }
      });
  }

  private async loadLocalDevResults(rawResults: RequirementsSolved[]): Promise<void> {
    const DEV_JOB_ID = 'local-dev';

    const results: RequirementsSolved[] = rawResults.map((result, index) => {
      const patch: any = { ...result, id: `${DEV_JOB_ID}_${index}`, jobId: DEV_JOB_ID };
      if (patch.capability_curve === null || patch.capability_curve === undefined) {
        patch.capability_curve = { };
      }
      return patch;
    });

    this.selectedJob = DEV_JOB_ID;
    this.$resultsLoaded = false;
    this.results = [];
    this.requirementService.resetMergedData();
    this.plotService.setGraphData([]);
    this.requirementService.jobResultLoading.next(JobLoadingStatus.LOADING);
    this.requirementService.selectedTimeIndex.next(0);
    this._cdr.detectChanges();

    await this.plotService.clearGraph();

    try {
      const staticResults = results.filter(r => r.requirement_solved_type === ResultType.STATIC);
      const dynamicResults = results.filter(r => r.requirement_solved_type === ResultType.DYNAMIC);
      const dcResults = results.filter(r => r.requirement_solved_type === ResultType.DRIVE_CYCLE);

      if (staticResults.length) await this.requirementService.processStaticResultData(staticResults);
      if (dynamicResults.length) await this.requirementService.processDynamicResultData(dynamicResults);
      if (dcResults.length) await this.requirementService.processDriveCycleResults(dcResults);

      this.results = results;
      this.$resultsLoaded = true;
      this.requirementService.jobResultLoading.next(JobLoadingStatus.FINISHED);

      setTimeout(() => {
        const firstResult = results.find(r => !('error_code' in r && (r as any).error_code));
        if (firstResult) {
          this.selectResult(firstResult);
        }
      });

      this._cdr.markForCheck();
    } catch (error) {
      console.error('Error loading local dev results:', error);
      this.snackbar.error('Failed to load local results');
      this.requirementService.jobResultLoading.next(JobLoadingStatus.FINISHED);
    }
  }

  async onYAxisChange(yaxis: string): Promise<void> {
    const activeSection = this.state.value(ActiveSectionState);
    const params = activeSection?.urlParams || [];

    if (
      params &&
      params?.length > 1 &&
      params[0] === ConceptSubSection.RESULTS &&
      params[1] !== ConceptSubSection.COMPARISON_TOOL
    ) {
      const jobId = params[1];
      const job = await lastValueFrom(
        this.jobApiService.jobLoadHandler({ jobId })
      );

      this.loadResults(job, false, true);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
    this.requirementService.jobResultLoading.next(JobLoadingStatus.IDLE);
    this.deselectJob();
  }

  calcMaxHeight(): void {
    setTimeout(() => {
      const cliOffset =
        this.page.subsection === ConceptSubSection.RESULTS && this.page.resultId
          ? 20
          : 0;

      const resultsCompareBtnOffset =
        this.page.subsection === ConceptSubSection.RESULTS &&
        !this.page.resultId
          ? 20
          : 0;

      const selectSimilarResultsBtnOffset =
        this.page.subsection === ConceptSubSection.COMPARISON_TOOL ? 20 : 0;

      const offset =
        cliOffset + resultsCompareBtnOffset + selectSimilarResultsBtnOffset;

      this.maxHeight = this.elRef.nativeElement.offsetHeight - offset;
      this._cdr.detectChanges();
    }, 0);
  }

  protected async getDesignId(): Promise<string> {
    const instanceId = this.activeRoute.snapshot.paramMap.get('id');

    if (!instanceId) throw new Error('No design instance found');

    const instance = await lastValueFrom(
      this.designService.designInstanceLoadHandler({
        designInstanceId: instanceId,
      })
    );

    if (!instance) throw new Error('Error retrieving instance');

    return instance.designId as string;
  }

  protected async selectJob(job: Job): Promise<void> {
    if (this.compareCheckboxesVisible) {
      return this.onSelectJob(job);
    }
    const validStatuses = [
      JobStatus.jobStatus.FINISHED,
      JobStatus.jobStatus.FAILED,
      'COMPLETED',
    ];

    const finalStatus = job.finalStatus as string | undefined;
    const lastStatus = job.lastStatus as string | undefined;

    // Allow finished/failed/completed if either finalStatus OR lastStatus matches
    const isFinalStatusValid =
      validStatuses.includes(finalStatus ?? '') ||
      validStatuses.includes(lastStatus ?? '');

    // Trusted completion from external source
    let isTrustedComplete = false;
    if (job.jobId) {
      isTrustedComplete =
        ['COMPLETED', 'COMPLETE'].includes(this.trustedStatus[job.jobId]);
    }

    // Explicitly block running/null
    const isRunningOrUnknown =
      (!finalStatus && !lastStatus) ||
      finalStatus === JobStatus.jobStatus.RUNNING ||
      lastStatus === JobStatus.jobStatus.RUNNING;

    if ((!isFinalStatusValid && !isTrustedComplete) || isRunningOrUnknown) {
      return; // don’t load results yet
    }

    if (this.isComplete(job)) {
      const timeTaken = this.calculateJobDuration(job)
      if (timeTaken == null) {
        this.selectedJobDuration.emit('');
      }
      else {
      const formatted = formatJobDuration(timeTaken);
        this.jobDurations[job.jobId as string] = formatted;
      this.selectedJobDuration.emit(formatted);}
    } else {
      this.selectedJobDuration.emit('');
    }

    if (!this.designId) {
      this.designId = await this.getDesignId();
    }

    if (
      job.finalStatus === JobStatus.jobStatus.FAILED ||
      job.lastStatus === JobStatus.jobStatus.FAILED
    ) {
      const errs = await lastValueFrom(
        this.jobApiService.errorListPageHandler({ jobId: job?.jobId as string })
      );

      const error =
        (errs as ErrorListPageResponse).errors?.[0]?.errorCode ?? '';
      this.dialog.open(JobErrorDialogComponent, {
        title: `Job Error: ${error}`,
        data: {
          ...errs,
          jobId: job?.jobId as string,
          jobName: job?.jobName as string,
          jobDockerTag: job?.dockerTag as string,
          designId: this.designId as string,
        },
      });
    } else {
      const section = this.state.value(ActiveSectionState);
      if (section?.id) {
        this.actions.execute(NavigateAction, [
          ...(section.userData?.urlTree ?? []),
          'results',
          job.jobId + '',
        ]);
      }

      this.requirementService.resetDefaultLayoutRange();
      this.requirementService.jobResultLoading.next(JobLoadingStatus.LOADING);
      this.selectedJob = job.jobId as string;
      this.$resultsLoaded = false;
      this.requirementService.resetMergedData();
      this.plotService.setGraphData([]);
      this.results = [];
      this._cdr.detectChanges();

      this.loadResults(job);
    }

    return Promise.resolve();
  }

  protected isFailed(job: any): boolean {
    return (
      this.barlabels[job.jobId] === 'FAILED' || job.finalStatus === 'FAILED'
    );
  }

  protected isComplete(job: any): boolean {
    return (
      this.barlabels[job.jobId] === 'COMPLETED' ||
      job.finalStatus === 'COMPLETED'
    );
  }

  protected deselectJob(redirect?: boolean): void {
    this.selectedJob = '';
    this.selectedResult = '';
    this.results = [];
    this.requirementService.setSelectedResult(null);
    this.requirementService.resetMergedData();
    this.requirementService.jobResultLoading.next(JobLoadingStatus.IDLE);
    this.requirementService.setSelectedResultCliVersion('');
    this.selectedJobDuration.emit('');

    if (redirect) {
      const section = this.state.value(ActiveSectionState);
      if (section?.id) {
        const activeSection = this.state.value(ActiveSectionState);
        this.actions.execute(NavigateAction, [
          ...(activeSection?.userData?.urlTree ?? []),
          'results',
        ]);
      }
    }

    this._cdr.markForCheck();
  }

  protected selectResult(result: any, forceRangeRefresh: boolean = false): void {
    if (!result) {
      return;
    }

    this.selectedResult = result.id;
    this.requirementService.selectedTimeIndex.next(0);
    this.requirementService.selectResult(
      result,
      this.cliVersion,
      result.requirement_solved_type === ResultType.DRIVE_CYCLE,
      this.selectedJob,
      () => {
        return this.selectedJob;
      },
      forceRangeRefresh
    );

    this.cevActions.execute(PageStateExecuteEnum.SET_RESULT_ID, result.id);
    this.cevActions.execute(
      PageStateExecuteEnum.SET_SUBSECTION,
      ConceptSubSection.RESULTS
    );
    this.cevActions.execute(
      PageStateExecuteEnum.SET_COMPARISON_TOOL_JOB_IDS,
      null
    );

    this._cdr.detectChanges();
  }

  protected onRenameJob($event: Event, job: Job): void {
    $event.stopPropagation();

    const dialog = this.dialogService.open(UpdateOneInputComponent, {
      title: 'Rename job',
      data: {
        value: job.jobName,
        inputType: 'text',
        label: 'Job Name',
        placeholder: 'Enter new job name',

        onConfirm: async (newJobName: string) => {
          this.resultsService
            .jobUpdateName(job.jobId as string, newJobName)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
              next: (response: Job) => {
                this.updateJob(response);
                dialog.close();
              },
              error: (err) => console.error('Error:', err),
            });
        },
      },
    });
  }

  protected onSelectJob(job: Job): void {
    if (job.jobId === undefined) {
      console.error('Job ID is undefined', job);
      return;
    }

    if (!['COMPLETED'].includes((job.finalStatus || job.lastStatus) ?? '')) {
      console.error('Job is not complete', job);
      return;
    }

    if (!this.selectedItems.includes(job?.jobId + '')) {
      this.selectedItems.push(job.jobId);
    } else {
      const index = this.selectedItems.indexOf(job.jobId);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
    }

    this.selectedItemsChange.emit(this.selectedItems);
  }

  protected onDeleteJob($event: Event, job: Job): void {
    $event.stopPropagation();
    const cancelJobStatus = this.isCancellable(job);

    const title =
      (cancelJobStatus ? 'Cancel' : 'Delete') +
      ' job' +
      (job.jobName ? `: ${job.jobName}` : '');

    const dialog = this.dialogService.open(ConfirmComponent, {
      title,
      data: {
        onConfirm: async () => {
          if (cancelJobStatus) {
            const loadingRef = this.dialog.open(LoadingComponent, {
              title: 'Cancelling',
            });

            lastValueFrom(this.resultsService.cancelJob(job.jobId as string))
              .then(() => {
                const concept = this.state.value(ActiveConceptState);

                if (concept) {
                  const jobId = job.jobId as string;

                  // Update job status to CANCELLED now rather than wait for websocket
                  this.actions.execute(UpdateJob, {
                    ...job,
                    simulations: [
                      { ...job.simulations?.[0], lastStatus: 'CANCELLED' },
                    ],
                  });

                  this.actions.execute(JobCancelProcessing, jobId);

                  if (!concept.jobs_ids.includes(jobId)) {
                    const updatedConcept = {
                      ...concept,
                      jobs_ids: [...concept.jobs_ids, jobId],
                    };

                    this.state.set(ActiveConceptState, updatedConcept);
                    this.actionService.execute(LoadAllJobs, updatedConcept);
                  } else {
                    this.actionService.execute(LoadAllJobs, concept);
                  }
                }
                loadingRef.close();
              })
              .catch(() => {
                this.snackbar.error('Failed to delete Job');
                dialog.close();
                throw Error('Failed to delete Job');
              });

            dialog.close();
          } else {
            const loadingRef = this.dialog.open(LoadingComponent, {
              title: 'Deleting',
            });

            await lastValueFrom(
              this.resultsService.cancelJob(job.jobId as string)
            );

            lastValueFrom(
              this.jobService.deleteJobEndpointJobsDelete(
                this.state.value(SelectedAccountState),
                job.jobId as string,
                job.designId,
              )
            )
              .then(() => {
                loadingRef.close();

                const concept = this.state.value(ActiveConceptState);

                if (concept) {
                  // Remove the deleted job id from concept and reload all jobs
                  const updatedConcept = {
                    ...concept,
                    jobs_ids: concept.jobs_ids.filter(
                      (id: string) => id !== job.jobId
                    ),
                  };
                  this.state.set(ActiveConceptState, updatedConcept);
                  this.actionService.execute(LoadAllJobs, updatedConcept);
                }
              })
              .catch(() => {
                this.snackbar.error('Failed to delete Job');
                loadingRef.close();
              });

            dialog.close();
          }
        },
        onCancel: () => {
          dialog.close();
        },
      },
    });
  }

  private async loadResults(
    job: Job,
    selectFirstResult: boolean = true,
    forceRangeRefresh: boolean = false
  ): Promise<void> {
    this.displayService.updateProgressItem(0);
    await this.plotService.clearGraph();

    const hasResults = this.results?.length > 0;

    try {
      const { results, cliVersion } = hasResults
        ? await this.resultsService.getResults(job, false, this.results)
        : await this.resultsService.getResults(job);

      this.results = results;
      this.cliVersion = cliVersion ?? this.cliVersion;

      this.calcMaxHeight();
      this.$resultsLoaded = true;

      if (this.selectedJob.length) {
        this.requirementService.jobResultLoading.next(JobLoadingStatus.FINISHED);
      }

      if (!this.toupdate) {
        setTimeout(() => {
          const result = selectFirstResult
            ? this.results.find((r) => !('error_code' in r && (r as any).error_code))
            : this.results[parseInt(this.selectedResult.split('_').pop() || '0', 10)];

          this.selectResult(result, forceRangeRefresh);
        });
      }

      this.requirementService.selectedTimeIndex.next(0);
      this._cdr.markForCheck();
    } catch (error) {
      console.error('Error loading results:', error);
      this.snackbar.error('Failed to load results');
      this.requirementService.jobResultLoading.next(JobLoadingStatus.FINISHED);
      this.deselectJob(true);
    }
  }

  private async updateJobStatus(data: [string, string]): Promise<void> {
    const [jobId, status] = data;
    const job = this.jobsList.find((j) => j.jobId === jobId);
    this.trustedStatus[jobId] =
      job?.finalStatus?.toUpperCase() ||
      job?.simulations?.[0]?.lastStatus?.toUpperCase() ||
      status?.toUpperCase();

    switch (this.trustedStatus[jobId]) {
      case JobStatus.jobStatus.FAILED:
        this.loadingBar[jobId] = false;
        this.progressBars[jobId] = [
          { percentage: 100, color: ColorBackground.DANGER_DEFAULT },
        ];
        this.barlabels[jobId] = 'FAILED';
        break;

      case 'COMPLETE': /** AT This stage, the trusted status is COMPLETE from simulation last status but the job isn't finished till the lastStatus is COMPLETED (with a D) */
        this.loadingBar[jobId] = false;
        this.barlabels[jobId] = 'COMPLETED'; // TODO: When OCM Fix their lastStatus to COMPLETED (with a D), change this back to 'FINISHING UP' and loadingBar to true
        break;

      case 'RUNNING':
        this.barlabels[jobId] = 'RUNNING';
        break;

      default:
        if (['COMPLETED', 'CANCELLED'].includes(this.trustedStatus[jobId])) {
          this.loadingBar[jobId] = false;
          this.progressBars[jobId] = [
            { percentage: 100, color: ColorBackground.SUCCESS_DEFAULT },
          ];
        }

        this.barlabels[jobId] = this.trustedStatus[jobId];
        break;
    }

    if (job && !job.jobName) {
      const jobMetaMap = await this.actions.execute(JobMetaData);
      job.jobName = jobMetaMap.get(jobId);
      this.jobsList = [...this.jobsList];
    }

    this.updateJobProgress([jobId, undefined]);
  }

  private refreshAllJobs(): void {
    this.jobsList.forEach((job) => {
      this.updateJobFromStore(job);
    });
  }

  /**
   * Helps Angular track job items efficiently in *ngFor to prevent view errors
   * and unnecessary re-renders when jobsList updates.
   *
   * @param index
   * @param job
   * @protected
   */
  protected trackByJobId(index: number, job: Job): string {
    return job.jobId ?? `${index}`;
  }

  private updateJobFromStore(updatedJob: Job): void {
    this.jobsList = this.jobsList.map((job) => {
      if (job.jobId === updatedJob.jobId) {
        return updatedJob;
      }
      return job;
    });

    this.updateJobStatus([
      updatedJob.jobId as string,
      updatedJob.lastStatus as string,
    ]);
    this._cdr.detectChanges();
  }

  private updateJobProgress(data: [string, number | undefined]): void {
    const [jobId, progress] = data;
    const job = this.jobsList.find((j) => j.jobId === jobId);
    if (!this.progressBars[jobId]) {
      this.progressBars[jobId] = [];
    }

    if (!this.progressBars[jobId][0]) {
      this.progressBars[jobId][0] = {
        color: this.getProgressColour(jobId),
        percentage: 0
      };
    }

    this.progressBars[jobId][0].color = this.getProgressColour(jobId);

    if (!job?.finalStatus) {
      if (
        this.progressBars[jobId] &&
        this.progressBars[jobId][0] &&
        this.trustedStatus[jobId] !== 'COMPLETED' &&
        this.trustedStatus[jobId] !== 'COMPLETE' &&
        this.trustedStatus[jobId] !== 'QUEUED'
      ) {
        this.hasJobProgressSocket[jobId] = true;
        this.loadingBar[jobId] = true;
        this.progressBars[jobId] = [...this.progressBars[jobId]];
        if (progress && progress >= 0) {
          this.progressBars[jobId][0].percentage = progress;
          this.barlabels[jobId] = `RUNNING: ${Math.round(progress)}%`;
        }
      }
    }

    if (['COMPLETED', 'CANCELLED'].includes(this.trustedStatus[jobId])) {
      this.loadingBar[jobId] = false;
    }

    this._cdr.detectChanges();
  }

  private updateJobName(data: Map<string, string> | undefined): void {
    if (!data || data.size === 0) {
      return;
    }

    const [jobId, name] = Array.from(data.entries())[0];
    const job = this.jobsList.find((j) => j.jobId === jobId);
    if (job) {
      job.jobName = name;
    }

    this._cdr.detectChanges();
  }

  private setJobs(jobs: Job[]): void {
    this.progressBars = {};
    this.barlabels = {};
    if (jobs) {
      this.jobsList = jobs.sort(
        (j1, j2) =>
          (j2?.queuedStatusDate || j2?.lastStatusDate || Infinity) -
          (j1?.queuedStatusDate || j1?.lastStatusDate || Infinity)
      );
    } else if (this.jobsList.length) {
      this.jobsList = [];
    }

    this.jobsList.forEach(async (job) => {
      if (this.isComplete(job)) {
        const jobDuration = this.calculateJobDuration(job);
        if (jobDuration !== null) {
          this.jobDurations[job.jobId as string] = formatJobDuration(jobDuration);
        }
      }
      this.loadingBar[job.jobId as string] = false;

      const status = job.finalStatus || job.lastStatus;

      const jobRunning =
        status === JobStatus.jobStatus.RUNNING &&
        !this.hasJobProgressSocket[job.jobId as string];

      if (
        job.jobId &&
        (jobRunning ||
          status === JobStatus.jobStatus.QUEUED ||
          status === JobStatus.jobStatus.CREATED ||
          status === null)
      ) {
        this.loadingBar[job.jobId as string] = true;
        if (jobRunning) {
          this.barlabels[job.jobId as string] = 'FETCHING PROGRESS...';
        }
      }

      if (
        job?.simulations?.[0]?.lastStatus === 'COMPLETED' &&
        status !== 'FINISHED' &&
        status !== 'CANCELLED' &&
        status !== 'COMPLETED'
      ) {
        this.loadingBar[job.jobId as string] = true;
        this.barlabels[job.jobId as string] = 'FINISHING UP';
      }
      this.progressBars[job.jobId as string] = [
        {
          percentage: 100,
          color: this.getProgressColour(job.jobId as string),
        },
      ];
      this.progressBars[job.jobId as string] = [
        ...this.progressBars[job.jobId as string],
      ];
    });

    if (this.toupdate) {
      if (this.selectedResult && this.selectedJob.length) {
        this.selectJob(
          this.jobsList.find((j) => j.jobId === this.selectedJob) as Job
        ).then(() => {
          this.selectResult(
            this.results.find((r) => r.id === this.selectedResult)
          );
          this.toupdate = false;
        });
      }
    }

    this.getJobCurrentProgress();

    this.refreshAllJobs();

    this._cdr.detectChanges();
  }

  private getProgressColour(jobId: string): string {
    switch (this.trustedStatus[jobId]) {
      case 'FAILED':
        return ColorBackground.DANGER_DEFAULT;
      case 'CANCELLED':
        return ColorBackground.WARNING_DEFAULT;
      case 'RUNNING':
        return ColorBackground.WARNING_DEFAULT;
      default:
        return ColorBackground.SUCCESS_DEFAULT;
    }
  }

  private updateJob(updatedJob: Job): void {
    const index = this.jobsList.findIndex(
      (job) => job.jobId === updatedJob.jobId
    );
    if (index !== -1) {
      this.jobsList[index] = updatedJob;
      this._cdr.detectChanges();
    }
  }

  private getJobCurrentProgress(): void {
    this.jobsList.forEach((job) => {
      if (job.lastStatus === JobStatus.jobStatus.RUNNING) {
        firstValueFrom(this.resultsService.getJobProgress(job.jobId as string))
          .then((res) => {
            const progress = res.simulationProgressList.at(-1)?.progress || 0;
            this.updateJobProgress([job.jobId as string, progress]);
          })
          .catch((error) => {
            console.error(
              `Failed to get progress for job ${job.jobId}:`,
              error
            );
          });
      }
    });
  }

  getDeleteIcon(job: Job): any | null {
    if (this.isCancellable(job)) return this.Icons.CLOSE;

    const deletableStatuses = [
      JobStatus.jobStatus.FAILED,
      JobStatus.jobStatus.FINISHED,
      'CANCELLED',
      'COMPLETED',
      'COMPLETE',
      'complete',
    ];
    const status = this.trustedStatus[job.jobId + ''] ?? '';
    const notStarted =
      !job.finalStatus && !job.lastStatus && !job.simulations?.[0]?.lastStatus;

    if (deletableStatuses.includes(status) || notStarted)
      return this.Icons.DELETE;

    return null;
  }

  isCancellable(job: Job): boolean {
    const status = this.trustedStatus[job.jobId ?? ''] as JobStatus.jobStatus;
    return [
      JobStatus.jobStatus.CREATED,
      JobStatus.jobStatus.QUEUED,
      JobStatus.jobStatus.RUNNING,
    ].includes(status);
  }

  private getStatus(job: Job | undefined): JobStatus.jobStatus | undefined {
    const jobId = job?.jobId;
    if (!jobId) return undefined;
    return this.trustedStatus[jobId] as JobStatus.jobStatus;
  }

  getProgressState(job: Job | undefined): ProgressState {
    const jobId = job?.jobId;
    if (!jobId) return 'idle';

    if (this.getStatus(job) === jobStatus.RUNNING) {
      return 'running';
    }

    const loading = this.loadingBar[jobId];
    if (loading === undefined || loading) {
      return 'loading';
    }

    return 'idle';
  }

  private calculateJobDuration(job: Job | undefined): number | null {
    const jobStatusHistory = job?.jobStatus
    if (!jobStatusHistory || jobStatusHistory.length === 0) {
      return null;
    }

    const queuedEntry = jobStatusHistory.find(entry => entry.jobStatus === 'QUEUED');
    const finishedEntry = jobStatusHistory.find(entry => entry.jobStatus === 'FINISHED');

    if (!queuedEntry || !finishedEntry || !queuedEntry.jobStatusDate || !finishedEntry.jobStatusDate) {
      return null;
    }

    const queuedDate = new Date(queuedEntry.jobStatusDate).getTime();
    const finishedDate = new Date(finishedEntry.jobStatusDate).getTime();

    // Return difference in seconds
    return (finishedDate - queuedDate) / 1000;
  }
}
type ProgressState = 'loading' | 'running' | 'idle';
